import {createStore, filterNil} from '@ngneat/elf';
import {selectEntity, withEntities} from "@ngneat/elf-entities";
import {Injectable} from "@angular/core";
import {FeilhåndteringArgumenter, Feilhåndteringsstrategi, hentFraLokallagerEllerNull, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {firstValueFrom, Observable} from "rxjs";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {ModellSlettetHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellSlettetHendelseHåndteringBygger";
import {AvstemningId, AvstemningsstatusModell, ModelloppdateringType, MøteId, MøteRessursClient, OppdateringmodellAvstemning, OppdateringmodellAvstemningsresultatDeltakerEndret, OppdateringmodellAvstemningsresultatDeltakerNullstilt} from "../ressurser/generertNg2HttpKlient";
import {DataOgMeta, kunRequestRespons} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";
import {FeilhåndteringsstrategiForDataOgMeta, Leveringsvalg, registrerEntitet, serverFraLagerOgRequestPakketIMeta, somFeilhåndteringsstrategiForDataOgMeta} from "./datatjeneste";
import {HttpResponse} from "@angular/common/http";
import {utelatFørsteDersomNil} from "felles/ng2/rxjsHjelpere";
import {distinctUntilChangedDeep} from "../../støttefunksjoner/observable";
import {mapTilData} from "./DataOgMeta";

const debug = initDebugger('AvstemningsstatusDatatjeneste');

/** Datalager for {@link AvstemningsstatusModell}. */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class AvstemningsstatusDatatjeneste {

    /** Lokalt datalager for {@link AvstemningsstatusModell} entiteter.
     * Bruker avstemning.sakId som forelder.*/
    readonly avstemningsstatusLager = createStore({name: 'avstemningsstatus'}, withEntities<AvstemningsstatusModell, 'moeteId'>({idKey: 'moeteId'}));

    /** Holder rede på åpne subscriptions basert på avstemningsstatus.moeteId. Benyttes ifbm modelloppdateringer. */
    readonly avstemningstatusAbonnenter = new DatalagerAbonnenter<MøteId>('avstemningsstatus');

    constructor(private møteRessurs: MøteRessursClient,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private modelloppdateringer: Modelloppdateringer) {

        this.lyttTilModelloppdateringer();
    }

    /** Returnerer angitt avstemning fra lokallager. Returnerer null dersom ikke funnet */
    hentFraLokallager(møteId: MøteId): AvstemningsstatusModell | null {
        return hentFraLokallagerEllerNull(this.avstemningsstatusLager, møteId);
    }

    /** Erstatter angitt avstemningsstatus i lokalt datalager */
    erstattILokallager(avstemningsstatus: AvstemningsstatusModell): AvstemningsstatusModell {
        console.log('## erstattILokallager', avstemningsstatus, this.avstemningstatusAbonnenter);
        return registrerEntitet(this.avstemningsstatusLager, avstemningsstatus);
    }

    /** Laster avstemningsstatus for angitt møte.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    hentMedMeta(møteId: MøteId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<AvstemningsstatusModell>>;
    hentMedMeta(møteId: MøteId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<AvstemningsstatusModell | undefined>>;
    hentMedMeta(møteId: MøteId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<AvstemningsstatusModell | undefined>> {
        return this.avstemningstatusAbonnenter.observer(møteId,
          serverFraLagerOgRequestPakketIMeta({
              ressurskall: () => this.møteRessurs.avstemningsstatus(møteId),
              registrerILager: (respons: HttpResponse<AvstemningsstatusModell>) => registrerEntitet(this.avstemningsstatusLager, respons.body!),
              hentFraLager: () => this.avstemningsstatusLager.pipe(
                selectEntity(møteId),
                leveringsvalg == Leveringsvalg.ikke_lever_ved_slettet ? filterNil() : utelatFørsteDersomNil(),
                distinctUntilChangedDeep()), //kun lever verdier fra lageret ved reelle endringer
              ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente avstemningsstatus', 'hentMedMeta')
          }));
    }

    /** Laster avstemningsstatus for angitt møte.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    hent(møteId: MøteId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<AvstemningsstatusModell>
    hent(møteId: MøteId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<AvstemningsstatusModell | undefined>
    hent(møteId: MøteId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<AvstemningsstatusModell | undefined> {
        return this.hentMedMeta(møteId, leveringsvalg, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    /** Laster avstemningsstatus for angitt møte fra server og oppdaterer lokalt datalager med dette. */
    async relast(id: MøteId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<void> {
        await firstValueFrom(this.hentMedMeta(id, Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi).pipe(kunRequestRespons()));
    }

    //hjelpemetoder

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    private lyttTilModelloppdateringer() {
        this.lyttTilMøtedeltakerAvstemningsresultatEndret();
        this.lyttTilMøtedeltakerAvstemningsresultatNullstilt();
        this.lyttTilMøtedeltakereOppdatert();
        this.lyttTilMøteAvstemningÅpnet();
        this.lyttTilMøteAvstemningStengt();
        this.lyttTilAvstemningÅpnet();
        this.lyttTilAvstemningStengt();
        this.lyttTilMøteSlettet();
    }

    private lyttTilMøtedeltakerAvstemningsresultatEndret() {
        this.opprettetEllerOppdatertAvstemningsstatusHendelse<OppdateringmodellAvstemningsresultatDeltakerEndret>(ModelloppdateringType.MØTEDELTAKER_AVSTEMNINGSRESULTAT_ENDRET)
          .medAbonnentoppslagOpt(this.avstemningstatusAbonnenter, hendelseModell => hendelseModell.moeteId)
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøtedeltakerAvstemningsresultatNullstilt() {
        this.opprettetEllerOppdatertAvstemningsstatusHendelse<OppdateringmodellAvstemningsresultatDeltakerNullstilt>(ModelloppdateringType.MØTEDELTAKER_AVSTEMNINGSRESULTAT_NULLSTILT)
          .medAbonnentoppslag(this.avstemningstatusAbonnenter, hendelseModell => hendelseModell.moeteId)
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøtedeltakereOppdatert() {
        this.opprettetEllerOppdatertAvstemningsstatusHendelseMedMøteId(ModelloppdateringType.MØTEDELTAKERE_OPPDATERT)
          .medAbonnentoppslag(this.avstemningstatusAbonnenter, hendelseModell => hendelseModell)
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøteAvstemningÅpnet() {
        this.opprettetEllerOppdatertAvstemningsstatusHendelseMedMøteId(ModelloppdateringType.MØTE_AVSTEMNING_ÅPNET)
          .medAbonnentoppslagOpt(this.avstemningstatusAbonnenter, hendelseModell => hendelseModell)
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøteAvstemningStengt() {
        this.opprettetEllerOppdatertAvstemningsstatusHendelseMedMøteId(ModelloppdateringType.MØTE_AVSTEMNING_STENGT)
          .medAbonnentoppslagOpt(this.avstemningstatusAbonnenter, hendelseModell => hendelseModell)
          .oppdaterLagerVedHendelse();
    }

    private lyttTilAvstemningÅpnet() {
        this.opprettetEllerOppdatertAvstemningsstatusHendelse<OppdateringmodellAvstemning>(ModelloppdateringType.AVSTEMNING_ÅPNET)
          .medAbonnentoppslag(this.avstemningstatusAbonnenter, hendelseModell => hendelseModell.moeteId)
          .oppdaterLagerVedHendelse();
    }

    private lyttTilAvstemningStengt() {
        this.opprettetEllerOppdatertAvstemningsstatusHendelse<OppdateringmodellAvstemning>(ModelloppdateringType.AVSTEMNING_STENGT)
          .medAbonnentoppslag(this.avstemningstatusAbonnenter, hendelseModell => hendelseModell.moeteId)
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøteSlettet() {
        this.slettetHendelse(ModelloppdateringType.MØTE_SLETTET)
          .oppdaterLagerVedHendelse();
    }

    private opprettetEllerOppdatertAvstemningsstatusHendelse<HENDELSE_MODELL_TYPE extends { moeteId: MøteId }>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, AvstemningsstatusModell, 'moeteId'>(this.avstemningsstatusLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.moeteId)
          .medEntitetLaster(hendelseModell => this.møteRessurs.avstemningsstatus(hendelseModell.moeteId).råRespons());
    }

    private opprettetEllerOppdatertAvstemningsstatusHendelseMedMøteId(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<MøteId, AvstemningsstatusModell, 'moeteId'>(this.avstemningsstatusLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell)
          .medEntitetLaster(hendelseModell => this.møteRessurs.avstemningsstatus(hendelseModell).råRespons());
    }

    private slettetHendelse(hendelseType: ModelloppdateringType) {
        return new ModellSlettetHendelseHåndteringBygger<AvstemningId, AvstemningsstatusModell>(this.avstemningsstatusLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell);
    }
}
